import axios from 'axios';
import Toastify from 'toastify-js';
import 'toastify-js/src/toastify.css';
import { openModal, closeModal } from './modal';

/*
=====================================
  TEST TYPE = "hint"
=====================================
*/

export function initHintTest(container, testId) {
  const storageKey = 'hint_test_' + testId;

  // --- 1. Є готовий результат
  const saved = localStorage.getItem(storageKey);
  if (saved) {
    const parsed = JSON.parse(saved);
    finishHintRender(container, parsed.resultHTML);
    return;
  }

  // --- 2. Є прогрес
  const progress = sessionStorage.getItem(storageKey);
  if (progress) {
    const parsed = JSON.parse(progress);
    renderHintStep(container, storageKey, parsed);
    return;
  }

  // --- 3. Починаємо тест
  loadHintTest(container, storageKey, testId);
}

/*
------------------------------
  1. Завантаження тесту
------------------------------
*/
async function loadHintTest(container, storageKey, testId) {
  container.classList.add('loading');

  try {
    const response = await axios.post(
      '/wp-admin/admin-ajax.php',
      new URLSearchParams({
        action: 'load_test_hint',
        test_id: testId,
      })
    );

    if (!response.data.success) {
      throw new Error('invalid response');
    }

    const { questions, answer, desc, variants } = response.data.data;

    const state = {
      testId,
      index: 0,
      questions,
      answer,
      desc,
      variants: Array.isArray(variants) ? variants : [],
      mistakes: [],
    };

    sessionStorage.setItem(storageKey, JSON.stringify(state));
    renderHintStep(container, storageKey, state);
  } catch (e) {
    showToast('Помилка завантаження тесту', 'error');
    container.classList.remove('loading');
  }
}

/*
------------------------------
  2. Рендер поточного кроку
------------------------------
*/
function renderHintStep(container, storageKey, state) {
  container.classList.remove('loading', 'check');
  container.classList.add('noTop');

  const { index, questions, mistakes } = state;
  const total = questions.length;
  const question = questions[index];

  const inner = container.querySelector('.test__innercont');
  if (!inner) return;

  const progress = Math.round(((index + 1) / total) * 100);

  inner.innerHTML = `
    <div class="test__progress hint_proggress">
      <div class="test__question">
        <div class="test__questioninner">
          <h2 class="test__questtitle">${question}</h2>

          <div class="test__hint">
            <div class="hint__label">
              <input type="text" class="input__hint" placeholder="Ваш варіант">
              <ul class="hint__vars"></ul>
            </div>
            <ul class="hint__mistakes">
              ${mistakes.map(m => `<li>${m.answer}</li>`).join("")}
            </ul>
          </div>

          <button class="btn-def btn__hint">Відповісти</button>
        </div>
      </div>

      <div class="test__bottom">
        <div class="test__progressbar">
          <div class="progress" style="width:${progress}%;">
            <span>${index + 1} з ${total}</span>
          </div>
        </div>
      </div>
    </div>
  `;

  setupHintEvents(container, storageKey, state);
}

/*
------------------------------
  3. Обробка UI подій
------------------------------
*/
function setupHintEvents(container, storageKey, state) {
  const input = container.querySelector('.input__hint');
  const variantsList = container.querySelector('.hint__vars');
  const answerBtn = container.querySelector('.btn__hint');

  const list = Array.isArray(state.variants) ? state.variants : [];

  // Автопідказки
  input.addEventListener('input', () => {
    const val = normalize(input.value);
    variantsList.innerHTML = '';

    if (!val) return;

    const filtered = list.filter(v => normalize(v).includes(val));

    variantsList.innerHTML = filtered
      .slice(0, 10)
      .map(v => `<li>${v}</li>`)
      .join('');
  });

  // Вибір з підказки
  variantsList.addEventListener('click', e => {
    if (e.target.tagName === 'LI') {
      input.value = e.target.textContent.trim();
      variantsList.innerHTML = '';
    }
  });

  // Відповідь
  answerBtn.addEventListener('click', () => {
    const val = input.value.trim();
    if (!val) {
      showToast('Введіть відповідь', 'error');
      return;
    }

    processHintAnswer(container, storageKey, state, val);
  });
}

/*
------------------------------
  4. Обробка відповіді
------------------------------
*/
function processHintAnswer(container, storageKey, state, userAnswer) {
  const normalizedUser = normalize(userAnswer);
  const normalizedCorrect = normalize(state.answer);

  // Правильно
  if (normalizedUser === normalizedCorrect) {
    finishHintTest(container, storageKey, state, true);
    return;
  }

  // Додаємо помилку
  state.mistakes.push({
    qIndex: state.index, // Номер використаної підказки
    answer: userAnswer, // Відповідь користувача
  });

  // Є ще підказки
  if (state.index < state.questions.length - 1) {
    state.index++;

    sessionStorage.setItem(storageKey, JSON.stringify(state));
    renderHintStep(container, storageKey, state);
  } else {
    // Закінчилися підказки
    finishHintTest(container, storageKey, state, false);
  }
}

/*
------------------------------
  5. Завершення тесту
------------------------------
*/
async function finishHintTest(container, storageKey, state, success) {
  container.classList.add('loading');

  const attempts = success ? state.index + 1 : state.questions.length;

  const postData = {
    action: 'finish_test_hint',
    test_id: state.testId,
    attempts,
    success: success ? 'true' : 'false',
    mistakes: JSON.stringify(state.mistakes),
    correct_q_index: state.index, // ← ДОДАНО
  };

  try {
    const response = await axios.post(
      '/wp-admin/admin-ajax.php',
      new URLSearchParams(postData)
    );

    if (!response.data.success) {
      throw new Error('invalid response');
    }

    const html = response.data.data;

    // Зберігаємо результат
    localStorage.setItem(storageKey, JSON.stringify({ resultHTML: html }));
    sessionStorage.removeItem(storageKey);

    finishHintRender(container, html);
  } catch (e) {
    showToast('Помилка завершення тесту', 'error');
    container.classList.remove('loading');
  }
}

/*
------------------------------
  6. Рендер результату
------------------------------
*/
function finishHintRender(container, html) {
  let inner = container.querySelector('.test__innercont');

  // Якщо контейнера немає — створюємо
  if (!inner) {
    inner = document.createElement('div');
    inner.classList.add('test__innercont');
    container.appendChild(inner);
  }

  inner.innerHTML = html;

  container.classList.remove('loading', 'check', 'noTop');

  // Ретест
  const retest = inner.querySelector('.test__retest');
  retest?.addEventListener('click', () => {
    const key = 'hint_test_' + container.getAttribute('data-testid');
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);

    inner.innerHTML = '';
    location.reload();
  });

  // Модалки
  inner.querySelectorAll('.open-modal').forEach(btn => {
    btn.addEventListener('click', () => openModal(btn.dataset.id));
  });

  inner.querySelectorAll('.close-modal').forEach(btn => {
    btn.addEventListener('click', () => {
      const m = btn.closest('.modal');
      if (m) closeModal(m);
    });
  });
}

/*
------------------------------
  Helpers
------------------------------
*/

function normalize(str) {
  return str
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

function showToast(message, type = 'error') {
  Toastify({
    text: message,
    duration: 3000,
    gravity: 'top',
    position: 'right',
    style: {
      background: type === 'error' ? '#e74c3c' : '#27ae60',
      borderRadius: '.5rem',
      padding: '.7rem 1rem',
    },
  }).showToast();
}
